import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useWeather } from '@/hooks/useWeather';
import { Cloud, Droplets, Sun, Wind } from 'lucide-react';
import useTranslation from '@/hooks/use-translation';
import { cn } from '@/lib/utils';

export function WeatherAnalysis() {
  const { weatherData, isLoading, error } = useWeather();
  const { translate } = useTranslation();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{translate("Weather Analysis")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-4 bg-muted rounded w-2/3" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{translate("Weather Analysis")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-500">{translate("Failed to load weather data")}</p>
        </CardContent>
      </Card>
    );
  }

  if (!weatherData) {
    return null;
  }

  const getWateringAdvice = () => {
    const { temperature, humidity, rainfall } = weatherData;
    
    if (rainfall > 0) {
      return {
        text: translate("Currently raining. No irrigation needed."),
        icon: <Cloud className="h-5 w-5 text-blue-500" />
      };
    }

    if (temperature > 30) {
      return {
        text: translate("High temperature detected. Consider evening irrigation."),
        icon: <Sun className="h-5 w-5 text-amber-500" />
      };
    }

    if (humidity > 70) {
      return {
        text: translate("High humidity detected. Reduce irrigation."),
        icon: <Droplets className="h-5 w-5 text-blue-500" />
      };
    }

    return {
      text: translate("Normal conditions. Follow regular schedule."),
      icon: <Wind className="h-5 w-5 text-green-500" />
    };
  };

  const advice = getWateringAdvice();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{translate("Weather Analysis")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <WeatherStat
              label={translate("Temperature")}
              value={`${weatherData.temperature}°C`}
              icon={<Sun className="h-5 w-5 text-amber-500" />}
            />
            <WeatherStat
              label={translate("Humidity")}
              value={`${weatherData.humidity}%`}
              icon={<Droplets className="h-5 w-5 text-blue-500" />}
            />
            <WeatherStat
              label={translate("Rainfall")}
              value={`${weatherData.rainfall}mm`}
              icon={<Cloud className="h-5 w-5 text-blue-500" />}
            />
            <WeatherStat
              label={translate("Forecast")}
              value={weatherData.description}
              icon={<Wind className="h-5 w-5 text-green-500" />}
            />
          </div>

          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              {advice.icon}
              <div>
                <h4 className="font-medium mb-1">{translate("Irrigation Advice")}</h4>
                <p className="text-sm text-muted-foreground">{advice.text}</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function WeatherStat({ label, value, icon }: { 
  label: string; 
  value: string | number; 
  icon: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
      {icon}
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );
} 